import type { TranslateService } from '@ngx-translate/core';
import type { WebWidgetModel, WidgetMetaData } from '@web/types';
import type {
    AuthorizationButtonData,
    LoginEduFormTrigger,
    RegistrationButtonData,
} from '@web/widgets/login-edu/type/login-edu-widget.type';
import type { LoginEduWidgetData } from '@web/widgets/login-edu/type/login-edu-widget.type';

import { EButtonWidthType, Widgets } from '@common/enums';
import { defaultWidgetParams, defaultWidgetPermission } from '@common/constants';
import { defaultWidgetDataAdapter } from '@common/helpers';
import { buttonIconTemplates } from '@common/icons/button-icons';

import { createLoginEduWidget } from '@sites/entity-core';

import {
    DEFAULT_BUTTON_ADDITIONAL_EFFECTS,
    DEFAULT_BUTTON_ICON,
} from '@web/widgets/common/base/base-button/base-button.const';

import { LoginEduViewComponent } from '@web/widgets/login-edu/components/login-edu-view/login-edu-view.component';
import { LoginEduControlComponent } from '@web/widgets/login-edu/components/login-edu-control/login-edu-control.component';

function buildTriggerFromAuthorization(button: AuthorizationButtonData): LoginEduFormTrigger {
    return {
        isActive: button.isActive,
        label: button.label,
        styleType: button.styleType,
        size: button.size,
        buttonBase: button.buttonBase,
        buttonHover: button.buttonHover,
        widthType: EButtonWidthType.auto,
        horizontalFormWidthType: EButtonWidthType.auto,
        widthValue: 260,
        buttonIcon: { ...DEFAULT_BUTTON_ICON },
        additionalEffects: { ...DEFAULT_BUTTON_ADDITIONAL_EFFECTS },
        isIndependentCorners: button.isIndependentCorners,
        advancedRadius: button.advancedRadius,
        isShadow: button.isShadow,
        shadowSettings: button.shadowSettings,
    };
}

function buildTriggerFromRegistration(button: RegistrationButtonData): LoginEduFormTrigger {
    const inner = button.registrationButton;
    return {
        isActive: button.isActive,
        label: inner.label,
        styleType: inner.styleType,
        size: inner.size,
        buttonBase: button.buttonBase,
        buttonHover: button.buttonHover,
        widthType: EButtonWidthType.auto,
        horizontalFormWidthType: EButtonWidthType.auto,
        widthValue: 260,
        buttonIcon: { ...DEFAULT_BUTTON_ICON },
        additionalEffects: { ...DEFAULT_BUTTON_ADDITIONAL_EFFECTS },
        isIndependentCorners: button.isIndependentCorners,
        advancedRadius: button.advancedRadius,
        isShadow: button.isShadow,
        shadowSettings: button.shadowSettings,
    };
}

// DEFAULT COMPONENT SETTINGS — sourced from the canonical entity factory in
// @sites/entity-core. The package owns the data shape; host-only concerns —
// the i18n strings and the default button-icon SVG — are passed in via
// `CreateLegacyLoginEduWidgetOptions`, mirroring form/payment-form.
//
// `data` is a function so `WebWidgetsFactoryService.prepareWidgetData` invokes
// it with `meta` — a login-edu widget dropped into a zero block starts from the
// zero variant's defaults (`type: 'zero'` envelope tag, extended-position grid).
const loginEduData = (translateService: TranslateService, meta?: WidgetMetaData): LoginEduWidgetData => {
    const options = {
        texts: {
            authorizationLabel: translateService.instant('control_authorization'),
            registrationLabel: translateService.instant('control_registration'),
            enrollLabel: translateService.instant('control_enroll'),
            enterCourseLabel: translateService.instant('control_enter_course'),
            checkboxLabel: translateService.instant('control_registration_checkbox'),
            analyticsLeadSource: translateService.instant('control_edu_registration_lead_sourse'),
        },
        buttonIcon: buttonIconTemplates.telegram,
    };

    // The overloads on `createLoginEduWidget` are picked per literal arg —
    // calling each branch separately keeps overload resolution happy.
    const entity =
        meta?.type === 'zero' ? createLoginEduWidget('zero', options) : createLoginEduWidget('default', options);

    return entity.data as unknown as LoginEduWidgetData;
};
// END DEFAULT SETTINGS

export const loginEduWidgetModel: WebWidgetModel = {
    componentView: LoginEduViewComponent,
    componentControl: LoginEduControlComponent,
    name: Widgets.LOGIN_EDU,
    label: 'widget_login_edu_label',
    data: loginEduData,
    // Kept after the signal-form migration: `withDefaults` in
    // `WebLegacyWidgetSignalFormService` would backfill a *generic* `modalTrigger`,
    // whereas this derives the trigger from the button's own styling, so a
    // pre-trigger widget keeps its look when the modal view is switched on.
    dataAdapter: (data, modelData) => {
        const loginData = data as unknown as LoginEduWidgetData;
        const buttons = (loginData?.buttons ?? []).map((button) => {
            if (button?.buttonType === 'authorization' && !button.modalTrigger) {
                return { ...button, modalTrigger: buildTriggerFromAuthorization(button) };
            }
            if (button?.buttonType === 'registration' && !button.modalTrigger) {
                return { ...button, modalTrigger: buildTriggerFromRegistration(button) };
            }
            return button;
        });

        return defaultWidgetDataAdapter({ ...loginData, buttons }, modelData);
    },
    permission: defaultWidgetPermission,
    params: {
        ...defaultWidgetParams,
        isEducation: true,
        disabled: true,
        disabledTooltip: 'edu_widget_disabled_tooltip',
        type: ['default', 'specific', 'zero'],
    },
    // Control + view components own the form via `WebLegacyWidgetSignalFormService`;
    // the resolver takes the minimal load path (just `widget` / `isSpecific` /
    // `isEduSite` — no `widget$`, no `container$`, no `controlForm`).
    signalForm: true,
};
